import { wrap } from '@repo/di-ez';
import { clsx } from 'clsx';
import { secondsInYear } from 'date-fns/constants';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { HeaderColumn as HeaderColumnItem } from '../../../../features/header/data/model/header-column';
import { MonthHeaderColumn, YearHeaderColumn } from '../../../../features/header/data/model/header-column';
import { useMovableElement } from '../../../../shared/use-movable-element';
import type { Viewport } from '../../../../shared/viewport/viewport';

import styles from './header-column.module.scss';

export interface Props {
  item: HeaderColumnItem;
  horizontalViewport: Viewport;
  className?: string;
}

export const HeaderColumn = wrap(function Column({ className, item, horizontalViewport }: Props) {
  const { t } = useTranslation('drillingCarpet', { keyPrefix: 'Chart' });

  const ref = useMovableElement({ item: { x: { start: item.start, end: item.end } }, horizontalViewport });

  const renderColumn = (): ReactNode => {
    if (item instanceof MonthHeaderColumn) {
      return t(`months.${item.id}`);
    }

    if (item instanceof YearHeaderColumn) {
      return item.id;
    }

    return null;
  };

  if (item instanceof MonthHeaderColumn) {
    return (
      <div ref={ref} className={clsx(className, styles.titleWrapper)}>
        <p className={styles.title}>{renderColumn()}</p>
      </div>
    );
  } else if (item instanceof YearHeaderColumn) {
    const timelineYearsCount = horizontalViewport.length / secondsInYear;

    if (timelineYearsCount > 7.01) {
      return (
        <div ref={ref} className={clsx(className, styles.titleWrapper)}>
          <p className={styles.title}>{renderColumn()}</p>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        style={{ pointerEvents: 'none' }}
        className={clsx(className, styles.titleWrapper, styles.titleWrapper_year)}
      >
        <p
          className={clsx(styles.title, 'dash-body-regular')}
          style={{ pointerEvents: 'none', paddingTop: 0, width: 'max-content' }}
        >
          {item.id}
        </p>
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: 'calc(100% - 20px)',
            marginTop: 20,
            display: 'flex',
            flexFlow: 'row nowrap',
            pointerEvents: 'none',
          }}
        >
          {item.monthHeaderColumns.map((monthColumn) => {
            const width = (monthColumn.end - monthColumn.start) / (item.end - item.start);

            const monthLocale = (() => {
              if (timelineYearsCount <= 2) {
                return t(`months.${monthColumn.id}`);
              }

              return t(`months.${monthColumn.id}`).slice(0, 1);
            })();

            return (
              <div
                key={monthColumn.id}
                className={clsx(className, styles.titleWrapper, styles.titleWrapper_month)}
                style={{ position: 'static', width: `${width * 100}%` }}
              >
                <p className={clsx(styles.title, 'description')} style={{ paddingTop: 0, overflow: 'hidden' }}>
                  {monthLocale}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  console.error('Unknown header column type', item);

  return null;
});
